import { call, put, select, takeEvery } from 'redux-saga/effects';
import { captureException } from '@sentry/nextjs';
import { selectMatchedStrategyCollection } from '../../../../Helpers/strategyCollection.helpers';
import { selectCurrentStepKey } from '../../../../Helpers/clientApiJourneyPosition.helpers';
import ActionTypes from '../../../../Data/ActionTypes';
import FetchStatus from '../../../../Data/FetchStatus';
import { incrementMetric } from '../../../../Services/Datadog';
import { getErrorTypeTag } from '../clientApiError.helpers';
import { getStepApi } from '../clientApiStep.helpers';
import { resolveDependencies } from '../clientApiDependencies.helpers';
import {
  setSelectedSlot,
  setSlots,
  setSlotsStatus,
  setStepResponse,
} from '../../slices/clientApiJourney';
import { fetchClientApiSlots } from './fetchClientApiSlots';

export function* getClientApiSlotsWorker(action) {
  const { location, locationStepKey, refresh } = action.payload;
  const locationId = location?.id;

  try {
    // Re-opening a card re-lists its clinic for the server. Drop the stale slots
    // and any pick in this clinic first, so the card never shows an old grid and
    // the user cannot continue on a slot the fresh list has not confirmed.
    if (refresh) {
      const selectedSlot = yield select(
        (state) => state.clientApiJourney.appointment?.selectedSlot
      );
      yield put(setSlots({ locationId, slots: [] }));
      if (selectedSlot?.locationId === locationId) {
        yield put(setSelectedSlot({ selectedSlot: null }));
      }
    }

    yield put(
      setSlotsStatus({ locationId, slotsStatus: FetchStatus.IN_PROGRESS })
    );

    const matchedCollection = yield select(selectMatchedStrategyCollection);
    const stepKey = yield select(selectCurrentStepKey);
    const api = getStepApi({
      collection: matchedCollection,
      stepKey,
      operation: 'list',
    });

    if (!api?.url) {
      throw new Error(`No list operation on strategy step ${stepKey}`);
    }

    const submissionId = yield select((state) => state.submission?.id);
    const token = yield select((state) => state.submission?.token);
    const stepStates = yield select(
      (state) => state.clientApiJourney.stepStates
    );
    const stepResponses = yield select(
      (state) => state.clientApiJourney.stepResponses
    );

    // The picker lists slots per store: the expanded store stands in for the
    // location step's state on this call, without re-selecting it.
    const stepStatesForStore = locationStepKey
      ? { ...stepStates, [locationStepKey]: { location } }
      : stepStates;

    const body = resolveDependencies({
      dependencies: api.dependencies,
      ownKey: stepKey,
      stepStates: stepStatesForStore,
      stepResponses,
    });

    const { slots, response } = yield call(fetchClientApiSlots, {
      api,
      body,
      submissionId,
      token,
    });

    yield put(setSlots({ locationId, slots }));
    yield put(setStepResponse({ stepKey, operation: 'list', response }));
    yield put(
      setSlotsStatus({ locationId, slotsStatus: FetchStatus.COMPLETED })
    );
  } catch (error) {
    incrementMetric({
      name: 'getClientApiSlotsError',
      tags: { type: getErrorTypeTag(error) },
    });
    captureException(error);
    yield put(setSlotsStatus({ locationId, slotsStatus: FetchStatus.FAILED }));
  }
}

export default function* getClientApiSlotsSaga() {
  yield takeEvery(ActionTypes.GET_CLIENT_API_SLOTS, getClientApiSlotsWorker);
}
